'use client';

import { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

export default function BlogSpotCallbackPage() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authorization...');
  const [logs, setLogs] = useState<string[]>([]);
  const [countdown, setCountdown] = useState<number | null>(null);

  const addLog = (logMessage: string) => {
    console.log(logMessage + " - " + JSON.stringify(window.opener?.location || {}, null, 2));
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${logMessage}`]);
  }

  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      code: urlParams.get('code'),
      error: urlParams.get('error'),
      state: urlParams.get('state')
    };
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        addLog('Callback handler started.');
        const { code, error, state } = getUrlParams();
        addLog(`URL Params: code=${code}, error=${error}, state=${state}`);

        if (error) {
          setStatus('error');
          setMessage(`Authorization failed: ${error}`);
          addLog(`Error from Google: ${error}`);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No authorization code received from Google.');
          addLog('Error: No authorization code found in URL.');
          return;
        }

        addLog(`This window's origin: ${window.location.origin}`);

        // Try to communicate with parent window via postMessage first
        if (window.opener) {
          addLog(`Parent window opener found.`);
          try {
            const targetOrigin = window.location.origin;
            addLog(`Attempting to post message to target origin: ${targetOrigin}`);

            window.opener.postMessage({
              type: 'BLOGSPOT_AUTH_SUCCESS',
              code: code,
              state: state
            }, targetOrigin);
            
            addLog('Successfully posted message to parent window.');
          } catch (e) {
            addLog(`Failed to post message: ${e}`);
          }
        } else {
          addLog('window.opener is null - using localStorage fallback');
        }

        // Always use localStorage as backup/primary method
        const authData = {
          type: 'BLOGSPOT_AUTH_SUCCESS',
          code: code,
          state: state,
          sessionId: state, // Use the state parameter as session ID
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem('blogspot_auth_result', JSON.stringify(authData));
          addLog('Stored auth result in localStorage');
        } catch (e) {
          addLog(`Failed to store in localStorage: ${e}`);
        }

        setStatus('success');
        setMessage('Authorization successful!');
        
        // Start countdown timer
        setCountdown(15);
        const countdownInterval = setInterval(() => {
          setCountdown(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(countdownInterval);
              window.close();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setStatus('error');
        setMessage('An unexpected error occurred during callback processing.');
        addLog(`FATAL ERROR: ${errorMessage}`);
        console.error('OAuth callback error:', e);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing Authorization</h2>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Successful</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              {countdown !== null && (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-3">
                    This window will close automatically in {countdown} seconds
                  </p>
                  <button
                    onClick={() => window.close()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Close Now
                  </button>
                </div>
              )}
            </>
          )}
          {status === 'error' && (
            <>
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Authorization Failed</h2>
            </>
          )}
          
          <p className="text-gray-600">{message}</p>

          <div className="mt-6 text-left bg-gray-50 p-3 rounded-md border border-gray-200 max-h-48 overflow-y-auto">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Debug Logs:</h4>
            <pre className="text-xs text-gray-500 whitespace-pre-wrap break-all">
              {logs.join('\n')}
            </pre>
          </div>
          
          {status === 'success' && (
            <p className="text-sm text-gray-500 mt-4">This window will close automatically...</p>
          )}
          
          {status === 'error' && (
            <button
              onClick={() => window.close()}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Close Window
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
